import React, { useState, useCallback } from 'react';
import { get } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { FaSignInAlt, FaFolderOpen, FaRobot } from 'react-icons/fa';
import '../../styles/Auth.css';

const OAuthLogin = () => {
  const [authState, setAuthState] = useState({
    loading: false,
    error: null
  });

  const initiateOAuth = useCallback(async () => {
    setAuthState({ loading: true, error: null });
    
    try {
      // Get authentication session
      const authSession = await fetchAuthSession();
      const idToken = authSession.tokens?.idToken?.toString();
      
      if (!idToken) {
        throw new Error('No authentication token available');
      }

      // Request OAuth authorization URL
      const response = await get({
        apiName: 'mmfBackupApi',
        path: '/auth/initiate',
        options: {
          headers: {
            'Authorization': idToken
          }
        }
      });
      
      // Process the response
      const responseData = await response.response;
      const jsonData = await responseData.body.json();
      
      // Redirect to MMF for authentication
      if (jsonData.authUrl) {
        window.location.href = jsonData.authUrl;
      } else {
        throw new Error('Failed to get authentication URL');
      }
    } catch (err) {
      console.error('Error initiating OAuth flow:', err);
      setAuthState({ 
        loading: false, 
        error: err.message || 'Failed to initiate authentication' 
      });
    }
  }, []);
  
  return (
    <div className="oauth-login-container">
      <div className="auth-card">
        <div className="auth-logo">
          <FaFolderOpen className="auth-logo-icon" />
        </div>
        
        <h2>Connect Your MyMiniFactory Account</h2>
        
        <div className="auth-features">
          <div className="auth-feature">
            <div className="feature-icon">
              <FaFolderOpen />
            </div>
            <div className="feature-text">
              <h3>Organize Your Files</h3>
              <p>Create folders and manage your 3D models with ease</p>
            </div>
          </div>
          
          <div className="auth-feature">
            <div className="feature-icon">
              <FaRobot />
            </div>
            <div className="feature-text">
              <h3>AI Organization</h3>
              <p>Let AI automatically organize your files by type and theme</p>
            </div>
          </div>
        </div>
        
        <p className="auth-description">
          To organize your MyMiniFactory files, you need to authorize our app
          to access your account. We'll only access your file metadata to create a better organization system.
        </p>
        
        <button 
          className="mmf-login-button"
          onClick={initiateOAuth} 
          disabled={authState.loading}
          aria-busy={authState.loading}
        >
          <FaSignInAlt />
          {authState.loading ? 'Connecting...' : 'Connect to MyMiniFactory'}
        </button>
        
        {authState.error && (
          <div className="error-message" role="alert">
            {authState.error}
          </div>
        )}
        
        <div className="auth-info">
          <h3>What this means:</h3>
          <ul>
            <li>You'll be redirected to MyMiniFactory to log in</li>
            <li>You'll be asked to grant access to your files</li>
            <li>Once authorized, you'll be redirected back to this app</li>
            <li>Your files remain on MyMiniFactory's servers</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default OAuthLogin;